import { type ClassValue, clsx } from 'clsx';
import { isValidElement, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

export type { ClassValue } from 'clsx';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function assertNever(value: never, message = 'Unexpected value'): never {
  throw new Error(`${message}: ${String(value)}`);
}

export interface NormalizedOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/** Normalize strings or option objects to a consistent option shape. */
export function normalizeOptions(
  options: string[] | NormalizedOption[] | undefined,
): NormalizedOption[] {
  if (!options) return [];
  return options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt,
  );
}

/** Extract plain text from React children (for accessible labels, filtering, etc.) */
export function extractText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join(' ');
  if (isValidElement(node)) {
    const props = node.props as { children?: ReactNode };
    return extractText(props.children);
  }
  return '';
}
